/**
 * App-wide constants sourced from Vite env vars.
 * Single source of truth — every other module reads from here.
 */

export const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'AURA';
export const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '0.1.0';

export const CONVEX_URL = import.meta.env.VITE_CONVEX_URL ?? '';
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000';

export const CREDITS = [
  {
    name: import.meta.env.VITE_CREDIT_1_NAME ?? 'Sankar Balasubramanian',
    url: import.meta.env.VITE_CREDIT_1_URL ?? 'https://www.linkedin.com/in/sankar4/',
  },
  {
    name: import.meta.env.VITE_CREDIT_2_NAME ?? 'Jhansi G',
    url: import.meta.env.VITE_CREDIT_2_URL ?? 'https://www.linkedin.com/in/jhansig27/',
  },
] as const;

/** Acronym expansion — used on landing and in meta descriptions. */
export const ACRONYM =
  'Agentic UX-law based Reasoning architecture for AI-generated UI designs and deployable code';

/** The seven agents — used in the How-It-Works diagram and later in the pipeline UI. */
export const AGENTS = [
  {
    id: 'ora',
    name: 'ORA',
    full: 'Oracle of Requirements & Aspirations',
    role: 'reads the questionnaire, surfaces intent',
    model: 'Claude Sonnet 4.6',
  },
  {
    id: 'scry',
    name: 'SCRY',
    full: 'Sketch Cognition & Relational Yield',
    role: 'reads each lo-fi, extracts component trees',
    model: 'Gemini 3 Pro Vision',
  },
  {
    id: 'loom',
    name: 'LOOM',
    full: 'Layout Orchestrator & Ontology Mapper',
    role: 'weaves intent and sketches against the UX-KG',
    model: 'Claude Opus 4.7',
  },
  {
    id: 'lex',
    name: 'LEX',
    full: 'UX-Law Enforcement & eXamination',
    role: 'critiques every generator agent',
    model: 'Claude Sonnet 4.6',
  },
  {
    id: 'lumen',
    name: 'LUMEN',
    full: 'Lo-fi to Unified Mockup Engine',
    role: 'produces hi-fi mockup per page',
    model: 'Gemini 3 Pro Image',
  },
  {
    id: 'mosaic',
    name: 'MOSAIC',
    full: 'Modular Output for Site Assets, Icons & Covers',
    role: 'produces logos, hero art, section assets',
    model: 'Gemini 3 Pro Image',
  },
  {
    id: 'forge',
    name: 'FORGE',
    full: 'Front-end Orchestrated React Generator & Exporter',
    role: 'emits the deployable codebase, internally batched',
    model: 'Claude Opus 4.7',
  },
] as const;

export type AgentId = (typeof AGENTS)[number]['id'];

/** Routes — central manifest consumed by the router. */
export const ROUTES = {
  home: '/',
  projects: '/projects',
  projectWizard: (id: string) => `/projects/${id}/wizard`,
  /** Live pipeline and delivery are keyed by Convex job id (see routes.tsx). */
  jobPipeline: (jobId: string) => `/jobs/${jobId}`,
  jobDelivery: (jobId: string) => `/jobs/${jobId}/delivery`,
  terms: '/terms',
  privacy: '/privacy',
  /** Experimenter (admin) panel. */
  admin: '/admin',
} as const;
